import { useEffect, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import { authClient, type SessionUser } from "../lib/auth-client";
import { api } from "../lib/api-client";
import { useDocumentTitle } from "../lib/use-document-title";
import { Alert, SkeletonList, SkeletonPageHeader } from "../components/ui";
import { ProjectPicker } from "../components/project-picker";
import type { ProjectDto } from "@shared/types";

// The marketing LP now lives on its own origin; the Worker's "/" handler
// 302-redirects logged-out visitors there (see apps/api/src/index.ts). This
// constant is defense in depth for the SPA route case (e.g. client-side nav
// to "/" after a session expires without a full page load).
const LP_URL = "https://lp.elwood.akikan.app";

export function Home() {
  const location = useLocation();
  const { data: session, isPending } = authClient.useSession();
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectDto[] | null>(null);
  useDocumentTitle("");

  const uiMode = (session?.user as SessionUser | undefined)?.uiMode ?? "simple";

  const redirectSkeleton = (
    <section class="space-y-6">
      <SkeletonPageHeader />
      <SkeletonList count={2} rowClass="h-28" class="sm:grid-cols-2" />
    </section>
  );

  useEffect(() => {
    if (isPending || !session) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listProjects();
        if (cancelled) return;
        if (res.items.length === 0) {
          setError("プロジェクトが割り当てられていません。管理者に連絡してください。");
          return;
        }
        if (res.items.length === 1) {
          const own = res.items[0]!;
          const dest =
            uiMode === "simple" ? `/projects/${own.id}/pages` : `/projects/${own.id}`;
          location.route(dest, true);
          return;
        }
        // 2+ projects: let the user pick.
        setProjects(res.items);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, isPending, uiMode]);

  if (error) {
    return <Alert tone="danger">{error}</Alert>;
  }
  if (projects) {
    return <ProjectPicker projects={projects} uiMode={uiMode} />;
  }
  // Confirmed session → the effect above is about to redirect (single project)
  // or load the project picker; show a skeleton until it does.
  if (session) return redirectSkeleton;
  // No session yet. The Worker's "/" handler already 302-redirects logged-out
  // visitors to the standalone LP before this component ever mounts, so this
  // branch is only reachable via a client-side route change to "/" (e.g. a
  // session expiring mid-SPA-session). Redirect there too, as defense in
  // depth. Keep the skeleton up until the session state is confirmed so a
  // still-pending session doesn't briefly bounce a logged-in user.
  if (isPending) return redirectSkeleton;
  window.location.href = LP_URL;
  return redirectSkeleton;
}
